import { useState } from 'react';

import { CellClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';

type PseDay7ActionsProps = {
  pseDay7Data: any[];
  gridRef: React.RefObject<AgGridReact>;
  onUpdateStrawDetails: () => void;
  refetch: () => void;
};

export const PseDay7Actions = ({
  gridRef,
  onUpdateStrawDetails,
  refetch,
}: PseDay7ActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [params] = useSearchParams();
  const beginDate = params.get('beginDate');
  const siteId = params.get('site');

  const [loading, setLoading] = useState(false);

  const handleFrozenEmbryos = () => {
    const currentParams = new URLSearchParams(params);
    const queryString = currentParams.toString();

    navigate(`/day-7-transfer-pse/frozen-embryos?${queryString}`);
  };

  const handleShadeRows = () => {
    if (!gridRef.current?.api) return;

    const cellClasses = new Map<string, string>();
    let currentColor = 0;
    let previousKey = '';

    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      const plateNumber = node?.data?.plateNo;
      const fertilizationId = node?.data?.fertilizationId;
      const semenType = node?.data?.semenType;

      const currentKey = plateNumber || `${fertilizationId}-${semenType}`;

      if (previousKey && currentKey !== previousKey) {
        currentColor = 1 - currentColor;
      }

      cellClasses.set(
        node.id as string,
        currentColor ? 'bg-tog-light-green' : 'bg-tog-light-purple',
      );
      previousKey = currentKey;
    });

    const updatedColumns = gridRef.current.api.getColumns()?.map((col) => {
      const colDef = col.getColDef();

      return {
        ...colDef,
        cellClassRules: {
          ...(colDef.cellClassRules || {}),
          'bg-tog-light-purple': (params: CellClassParams) =>
            cellClasses.get(params.node.id as string) === 'bg-tog-light-purple',
          'bg-tog-light-green': (params: CellClassParams) =>
            cellClasses.get(params.node.id as string) === 'bg-tog-light-green',
        },
      };
    });

    if (updatedColumns) {
      gridRef.current.api.setGridOption('columnDefs', updatedColumns);
    }

    gridRef.current.api.refreshCells();
  };

  const updateStrawDetails = async () => {
    setLoading(true);

    try {
      onUpdateStrawDetails();
    } catch (error: any) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.serverError'),
          detail: t('straw.errorUpdatingStrawDetails'),
          life: 3000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-3">
      <Button icon="pi pi-refresh" onClick={refetch} />
      <Button label={t('embryo.frozenEmbryos')} onClick={handleFrozenEmbryos} />
      <Button label={t('mating.shade')} onClick={handleShadeRows} />

      <Button
        label={t('straw.updateStrawDetails')}
        severity="success"
        loading={loading}
        onClick={updateStrawDetails}
      />
    </div>
  );
};
